import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PROJECT_STATIONS, PRIORITIES, getLastUsedStation, setLastUsedStation } from "../lib/constants";
import CrewLeaderPicker from "../components/CrewLeaderPicker";
import SubstationCombobox from "../components/SubstationCombobox";
import AuthImg from "../components/AuthImg";
import {
  Send,
  ChevronLeft,
  Upload,
  X,
  CalendarClock,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const COMMON_EQUIPMENT = [
  "Meggar",
  "Vacuum Bottle Tester",
  "Insulation Resistance Tester",
  "Hipot Tester",
  "Boom Truck",
  "Crane",
  "Cable Puller",
];

export default function RequestForMaterial() {
  const nav = useNavigate();
  const { user, isViewer } = useAuth();

  React.useEffect(() => {
    if (isViewer) {
      toast.error("Regular crew members cannot submit material requests");
      nav("/", { replace: true });
    }
  }, [isViewer, nav]);

  const [form, setForm] = useState({
    Project_Station: getLastUsedStation() || PROJECT_STATIONS[0],
    Assigned_Crew_Leader: user?.name || "",
    Requestor_Message: "",
    Priority: "Normal",
    Contents: "",
    Minor_Materials: "",
    Equipment_Needed: "",
    Unavailable_Items: "",
    Desired_Date: "",
    Images: [],
  });
  const [equipChecks, setEquipChecks] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [error, setError] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleEquip = (name) => {
    const next = { ...equipChecks, [name]: !equipChecks[name] };
    setEquipChecks(next);
    const checked = Object.entries(next).filter(([, v]) => v).map(([k]) => k);
    // merge with any custom typed string in Equipment_Needed (custom items kept on a separate line)
    const customLines = (form.Equipment_Needed || "")
      .split("\n")
      .filter((l) => l.trim() && !COMMON_EQUIPMENT.includes(l.trim()));
    setForm((f) => ({
      ...f,
      Equipment_Needed: [...checked, ...customLines].join("\n"),
    }));
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImageUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setForm((c) => ({ ...c, Images: [...c.Images, data.path] }));
      }
      toast.success(`Uploaded ${files.length} image${files.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setImageUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (path) =>
    setForm((f) => ({ ...f, Images: f.Images.filter((p) => p !== path) }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.Requestor_Message.trim()) {
      setError("Tell the Yard Runner exactly what you need.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/shipments/request", form);
      setLastUsedStation(form.Project_Station);
      toast.success(`Request sent — MR ${data.MR_Number}`);
      nav(`/shipments/${data.id}`, { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Could not send request";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-32 md:pb-10">
      <button
        onClick={() => nav(-1)}
        data-testid="back-btn"
        className="flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-slate-600 hover:text-slate-900 mb-3"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      <div className="text-xs font-bold uppercase tracking-[0.2em] text-purple-700">
        Crew Field Request
      </div>
      <h1 className="font-display text-3xl sm:text-4xl uppercase text-slate-900 tracking-tight">
        Request for Material
      </h1>
      <p className="text-sm text-slate-600 mt-1 mb-6">
        Quick request to the Yard Runner. Tell them exactly what you need and when.
      </p>

      <form onSubmit={onSubmit} className="space-y-5" data-testid="request-form">
        <Field label="Project / Station *">
          <SubstationCombobox
            value={form.Project_Station}
            onChange={(v) => setField("Project_Station", v)}
            testid="req-project-station"
            extraOptions={PROJECT_STATIONS}
            required
          />
        </Field>

        <Field label="Assigned Crew Leader (you)">
          <CrewLeaderPicker
            value={form.Assigned_Crew_Leader}
            onChange={(v) => setField("Assigned_Crew_Leader", v)}
            testid="req-crew-leader-picker"
          />
        </Field>

        <Field
          label="What do you need? (required)"
          hint="Tell the Yard Runner exactly what you need and when."
        >
          <textarea
            value={form.Requestor_Message}
            onChange={(e) => setField("Requestor_Message", e.target.value)}
            data-testid="req-message"
            rows={4}
            className="w-full border-2 border-purple-400 rounded-sm px-3 py-2 text-base bg-white focus:border-slate-900 outline-none"
            placeholder="e.g. Need 4x 15kV lugs + a Meggar at TSS 12 before EOD Friday — termination work on Bay 3."
          />
        </Field>

        <Field label="Contents (main items)">
          <input
            value={form.Contents}
            onChange={(e) => setField("Contents", e.target.value)}
            data-testid="req-contents"
            className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
            placeholder="e.g. 4x 15kV switches"
          />
        </Field>

        <Field label="Minor materials (lugs, bolts, washers, etc.)">
          <textarea
            value={form.Minor_Materials}
            onChange={(e) => setField("Minor_Materials", e.target.value)}
            data-testid="req-minor-materials"
            rows={3}
            className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-base bg-white focus:border-slate-900 outline-none"
            placeholder="e.g. 24x bolts (1/2 in), 8 washers, 100ft fiber optic"
          />
        </Field>

        <Field label="Equipment needed">
          <div className="space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="req-equip-checks">
              {COMMON_EQUIPMENT.map((name) => {
                const checked = !!equipChecks[name];
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => toggleEquip(name)}
                    data-testid={`req-equip-${name.replace(/\s+/g, "-")}`}
                    className={`rg-tap rounded-sm border-2 px-3 text-sm font-bold uppercase tracking-wide flex items-center justify-center text-center ${
                      checked
                        ? "bg-amber-500 border-amber-700 text-slate-900"
                        : "bg-white border-slate-300 text-slate-700 hover:border-slate-900"
                    }`}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
            <textarea
              value={form.Equipment_Needed}
              onChange={(e) => setField("Equipment_Needed", e.target.value)}
              data-testid="req-equipment-needed"
              rows={2}
              className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-base bg-white focus:border-slate-900 outline-none"
              placeholder="Auto-fills from selections above. Add anything custom on a new line."
            />
          </div>
        </Field>

        <Field
          label="Desired date"
          hint="When do you need it on site?"
        >
          <div className="relative">
            <CalendarClock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="date"
              value={form.Desired_Date}
              onChange={(e) => setField("Desired_Date", e.target.value)}
              data-testid="req-desired-date"
              className="rg-tap w-full pl-9 pr-3 border-2 border-slate-300 rounded-sm text-base bg-white focus:border-slate-900 outline-none"
            />
          </div>
        </Field>

        <Field label="Priority" hint="Urgent items jump the queue.">
          <select
            value={form.Priority}
            onChange={(e) => setField("Priority", e.target.value)}
            data-testid="req-priority"
            className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none appearance-none"
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </Field>

        <Field
          label="Anything already known to be unavailable / short?"
          hint="the Yard Runner will see this in red on the queue."
        >
          <textarea
            value={form.Unavailable_Items}
            onChange={(e) => setField("Unavailable_Items", e.target.value)}
            data-testid="req-unavailable-items"
            rows={2}
            className="w-full border-2 border-rose-400 rounded-sm px-3 py-2 text-base bg-rose-50 focus:border-rose-700 outline-none"
            placeholder="e.g. We've used the last 4x 15kV lugs — please flag if not in stock"
          />
        </Field>

        <Field label="Photos (optional but helpful)">
          <div className="space-y-3">
            <label
              htmlFor="req-image-upload"
              className="rg-tap w-full bg-white border-2 border-dashed border-slate-400 hover:border-slate-900 rounded-sm flex items-center justify-center gap-2 font-bold uppercase tracking-wide text-slate-700 cursor-pointer"
              data-testid="req-image-label"
            >
              <Upload strokeWidth={2.5} className="h-5 w-5" />
              {imageUploading ? "Uploading…" : "Add photos"}
            </label>
            <input
              id="req-image-upload"
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              onChange={onUpload}
              className="hidden"
              data-testid="req-image-input"
            />
            {form.Images.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="req-image-thumbs">
                {form.Images.map((p) => (
                  <div key={p} className="relative aspect-square">
                    <AuthImg
                      path={p}
                      className="w-full h-full object-cover border-2 border-slate-300 rounded-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(p)}
                      className="absolute -top-2 -right-2 h-7 w-7 bg-rose-700 text-white grid place-items-center rounded-sm border-2 border-white"
                    >
                      <X strokeWidth={3} className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Field>

        {error && (
          <div
            className="border-2 border-rose-600 bg-rose-50 text-rose-800 text-sm font-bold px-3 py-2 rounded-sm flex items-start gap-2"
            data-testid="form-error"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        <div className="md:hidden h-20" />
        <div className="fixed md:static bottom-16 md:bottom-auto left-0 right-0 z-30 bg-white md:bg-transparent border-t-2 md:border-t-0 border-slate-300 p-4 md:p-0">
          <button
            type="submit"
            disabled={submitting || imageUploading}
            data-testid="send-request-btn"
            className="rg-tap-xl w-full bg-purple-700 hover:bg-purple-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 disabled:opacity-60 border-2 border-purple-900"
          >
            <Send strokeWidth={3} className="h-5 w-5" />
            {submitting ? "Sending…" : "Send Request to Yard Runner"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
        {label}
      </label>
      {hint && <div className="text-xs text-slate-500 mb-1">{hint}</div>}
      {children}
    </div>
  );
}
